import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { Chip } from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import * as React from "react";

import { AutocompleteType } from "@/shared/components/TagAndMetadataAutocomplete";
import { TagsEditor } from "@/shared/components/TagsEditor";
import {
  SkillAccessibility,
  SkillSummary,
  SkillVersionRecord,
} from "@/shared/domain/skills";
import { useUpdateSkillMetadata } from "@/shared/domain/skills/hooks";
// Its delete control is a real labelled button, which is what makes "reject
// this one" reachable without a mouse. The dashed box around proposed tags is
// what keeps them from reading as applied ones.
import { Tag as ProposedTag } from "@/shared/foundation/primitives";

import { EditableSkillName } from "./EditableSkillName";
import styles from "./SkillHeader.module.css";
import { VisibilityChip } from "./VisibilityChip";
import { VisibilitySelect } from "./VisibilitySelect";

dayjs.extend(relativeTime);

interface Props {
  summary: SkillSummary;
  /** Author, or a subscribed member of an `org-editable` skill. */
  canEdit: boolean;
  /** Accessibility is an ownership decision — author-only, even on org-editable. */
  isAuthor: boolean;
  orgId: string | undefined;
  /** The version on screen, named in the facts line. */
  version: SkillVersionRecord | null;
  /** Version picker and Edit button, right-aligned on the title row. */
  actions?: React.ReactNode;
  /**
   * AI-proposed tags awaiting the editor's Save — the `client_create_skill`
   * update handoff (see `SkillContentSeed`). Rendered alongside the real tags
   * but visibly unapplied, because until Save they aren't on the skill.
   * Already deduped against `summary.skill.tags` by the caller.
   */
  stagedTags?: string[];
  /**
   * Tags the same handoff proposed *removing*. Lifted out of the applied row
   * into a box of their own: leaving them in place would show the skill as
   * still carrying them, and marking them in place would make one row mean
   * two things.
   *
   * Already narrowed to tags the skill actually has by the caller.
   */
  stagedRemovals?: string[];
  /** Reject one staged addition. Omit to render them as un-dismissable. */
  onDropStagedTag?: (tag: string) => void;
  /** Reject one staged removal, putting the tag back. */
  onKeepTag?: (tag: string) => void;
}

/**
 * The skill's identity: its name, who made it, who can see it, and its tags.
 *
 * Every field here edits *in place* and saves on its own — renaming the skill,
 * retagging it, and changing its accessibility are three independent actions,
 * none of which involves a form mode or a dialog. That is deliberately separate
 * from editing a *version*, which is a body of prose worth an explicit
 * edit/save cycle (`?edit=1` and the Save buttons below). Conflating the two
 * made it unclear which fields a Save was about to write.
 *
 * Tags go through `put_tags` / `remove_tags` one at a time, so two people
 * tagging the same skill concurrently merge rather than clobber.
 */
export function SkillHeader({
  summary,
  canEdit,
  isAuthor,
  orgId,
  version,
  actions,
  stagedTags = [],
  stagedRemovals = [],
  onDropStagedTag,
  onKeepTag,
}: Props) {
  const updateMetadata = useUpdateSkillMetadata(orgId);
  const [error, setError] = React.useState<string | null>(null);
  const skillId = summary.skill.skill_id;
  // A tag staged for removal shows in the "to remove" box instead, so it isn't
  // also listed as applied — the skill would otherwise read as both keeping
  // and dropping it.
  const appliedTags = React.useMemo(
    () => summary.skill.tags.filter((tag) => !stagedRemovals.includes(tag)),
    [summary.skill.tags, stagedRemovals],
  );

  async function mutate(
    request: Parameters<typeof updateMetadata.mutateAsync>[0]["request"],
  ): Promise<void> {
    setError(null);
    await updateMetadata.mutateAsync({ skillId, request });
  }

  function changeAccessibility(next: SkillAccessibility) {
    if (next === summary.skill.accessibility) {
      return;
    }
    mutate({ accessibility: next }).catch((err: unknown) => {
      setError(
        err instanceof Error ? err.message : "Couldn't change accessibility.",
      );
    });
  }

  return (
    <header className={styles.header}>
      {/* Title and its actions share a row: the version picker and Edit button
          belong to the skill, not to the app's breadcrumb bar — which is where
          the agent page used to put its equivalents. */}
      <div className={styles.titleRow}>
        <EditableSkillName
          name={summary.skill.name}
          canEdit={canEdit}
          onRename={(name) => mutate({ name })}
        />
        {actions !== undefined && (
          <div className={styles.actions}>{actions}</div>
        )}
      </div>

      {/* Chips first, then prose — the same order the agent page's header uses,
          so the two are scannable in the same way. */}
      <div className={styles.facts}>
        {/* The same sparkle the chat composer's skill-invocation button uses,
            so a skill is recognizable by glyph wherever it turns up. */}
        <Chip
          size="small"
          variant="outlined"
          label="Skill"
          icon={<AutoAwesomeIcon />}
          className={styles.typeChip}
        />
        {isAuthor ? (
          <VisibilitySelect
            accessibility={summary.skill.accessibility}
            isPending={updateMetadata.isPending}
            onChange={changeAccessibility}
          />
        ) : (
          <VisibilityChip accessibility={summary.skill.accessibility} />
        )}
        <span className={styles.createdBy}>
          Created by <strong>{summary.skill.created_by}</strong>
        </span>
        {version !== null && (
          <>
            <span className={styles.separator} aria-hidden="true">
              ·
            </span>
            {/* Which version is on screen, and how stale it is. Both were
                previously only inferable from the version rail's highlight.
                The `dateTime` carries the exact instant the phrasing rounds. */}
            <time dateTime={version.modified}>
              v{version.version} updated {dayjs(version.modified).fromNow()}
            </time>
          </>
        )}
      </div>

      {/* Tags own their line: a chip strip that grows sideways would otherwise
          push the accessibility control around as tags are added. */}
      {(canEdit ||
        appliedTags.length > 0 ||
        stagedTags.length > 0 ||
        stagedRemovals.length > 0) && (
        <div className={styles.tags}>
          {/* The editor says itself when a tag fails to save. */}
          <TagsEditor
            tags={appliedTags}
            recordKey={skillId}
            autocompleteType={AutocompleteType.SkillTags}
            onAdd={(tag) => mutate({ put_tags: [tag] })}
            onRemove={(tag) => mutate({ remove_tags: [tag] })}
            readonly={!canEdit}
          />
          {/* Proposed changes sit in the tag row but inside dashed boxes of
              their own: they read as part of the skill's tags — which is what
              the user is deciding about — while the border and the caption
              keep them from being mistaken for what is already applied. */}
          {stagedTags.length > 0 && (
            <span
              className={styles.stagedTags}
              role="group"
              aria-label="Tags to add"
            >
              {stagedTags.map((tag) => (
                <ProposedTag
                  key={tag}
                  label={tag}
                  deleteLabel={`Don't add ${tag}`}
                  onDelete={
                    onDropStagedTag ? () => onDropStagedTag(tag) : undefined
                  }
                />
              ))}
              <small className={styles.stagedHint}>added when you save</small>
            </span>
          )}
          {stagedRemovals.length > 0 && (
            <span
              className={styles.stagedRemovals}
              role="group"
              aria-label="Tags to remove"
            >
              {stagedRemovals.map((tag) => (
                <ProposedTag
                  key={tag}
                  label={tag}
                  // Not "Remove <tag>": this button *cancels* the removal, so
                  // the default name would say the opposite of what it does.
                  deleteLabel={`Keep ${tag}`}
                  onDelete={onKeepTag ? () => onKeepTag(tag) : undefined}
                />
              ))}
              <small className={styles.stagedHint}>removed when you save</small>
            </span>
          )}
        </div>
      )}

      {error !== null && <small className={styles.error}>{error}</small>}
    </header>
  );
}
